import { useCallback, useRef, useEffect, useState } from 'react';
import optimistic from '../utils/optimisticNotifications';

/**
 * React hook for optimistic notifications with automatic cleanup
 * 
 * Usage:
 * const notify = useOptimisticNotifications();
 * 
 * // Simple usage
 * await notify.update('Account', updateAccountApi(id, data));
 * 
 * // With state management
 * await notify.update('Account', updateAccountApi(id, data), {
 *   revertAction: () => setAccounts(prev => prev.map(acc => acc.id === id ? originalAccount : acc))
 * });
 */
export const useOptimisticNotifications = () => {
  const activeNotificationsRef = useRef(new Set());

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Cancel all notifications created by this hook instance
      activeNotificationsRef.current.forEach(id => {
        optimistic.cancel(id);
      });
    };
  }, []);

  const trackNotification = useCallback((id) => {
    activeNotificationsRef.current.add(id);
    return id;
  }, []);

  const notify = useCallback({
    success: (successMessage, promise, errorMessage, options = {}) => {
      const id = options.id || `hook-${Date.now()}-${Math.random()}`;
      trackNotification(id);
      return optimistic.success(successMessage, promise, errorMessage, { ...options, id });
    },

    create: (itemName, promise, options = {}) => {
      const id = options.id || `create-${Date.now()}-${Math.random()}`;
      trackNotification(id);
      return optimistic.create(itemName, promise, { ...options, id });
    },

    update: (itemName, promise, options = {}) => {
      const id = options.id || `update-${Date.now()}-${Math.random()}`;
      trackNotification(id);
      return optimistic.update(itemName, promise, { ...options, id });
    },

    delete: (itemName, promise, options = {}) => {
      const id = options.id || `delete-${Date.now()}-${Math.random()}`;
      trackNotification(id);
      return optimistic.delete(itemName, promise, { ...options, id });
    },

    batch: (operations) => {
      // Track all batch operation IDs
      operations.forEach((op, index) => {
        const id = op.options?.id || `batch-${Date.now()}-${index}`;
        trackNotification(id);
        if (!op.options) op.options = {};
        op.options.id = id;
      });
      return optimistic.batch(operations);
    },

    info: (message, options = {}) => {
      return optimistic.info(message, options);
    }
  }, [trackNotification]);

  return notify;
};

/**
 * Hook for optimistic state management with automatic revert
 * 
 * Usage:
 * const [accounts, setAccounts, updateOptimistically] = useOptimisticState(initialAccounts);
 * 
 * // Update optimistically with automatic revert on failure
 * updateOptimistically(
 *   prev => prev.map(acc => acc.id === id ? { ...acc, name: newName } : acc),
 *   updateAccountApi(id, { name: newName }),
 *   'Account updated successfully!',
 *   'Failed to update account'
 * );
 */
export const useOptimisticState = (initialState) => {
  const [state, setState] = useState(initialState);
  const notify = useOptimisticNotifications();
  const previousStateRef = useRef(initialState);

  const updateOptimistically = useCallback(async (
    optimisticUpdate,
    promise,
    successMessage,
    errorMessage,
    options = {}
  ) => {
    // Store current state for potential revert
    previousStateRef.current = state;
    
    // Apply optimistic update
    const newState = optimisticUpdate(state);
    setState(newState);

    try {
      await notify.success(successMessage, promise, errorMessage, {
        ...options,
        revertAction: () => {
          setState(previousStateRef.current);
        }
      });
    } catch (error) {
      // Error already handled by notify.success (including revert)
      throw error;
    }
  }, [state, notify]);

  return [state, setState, updateOptimistically];
};

export default useOptimisticNotifications;